import React, { useState, useEffect } from 'react';
import { Box, Heading, Text, Button } from '@indeed/ifl-components';
import { ApolloError } from '@apollo/client';
import { GetDefaultApplyContactInfoQuery } from '../../pages/default-apply/contact-info/__generated__/ContactInfoPage.generated';

interface ContactInfoFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country: string;
    };
}

// Enhanced props interface for page-as-state pattern
interface ContactInfoFormProps {
    // Data from parent page - using real GraphQL type
    profileData?: GetDefaultApplyContactInfoQuery;
    profileLoading: boolean;
    profileError?: ApolloError;

    // Submission state
    isSubmitting: boolean;
    submitError: string | null;
    hasUnsavedChanges: boolean;

    // Flow navigation state
    canGoBack: boolean;

    // Callbacks to parent page
    onSubmit: (data: ContactInfoFormData) => Promise<void>;
    onChange: (hasChanges: boolean) => void;
    onBack: () => void;
}

const US_STATES = [
    { value: '', label: 'Select State' },
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
];

const ContactInfoForm: React.FunctionComponent<ContactInfoFormProps> = ({
    profileData,
    profileLoading,
    profileError,
    isSubmitting,
    submitError,
    hasUnsavedChanges,
    canGoBack,
    onSubmit,
    onChange,
    onBack,
}) => {
    // Form-only state (no Apollo, no navigation)
    const [formData, setFormData] = useState<ContactInfoFormData>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'US',
        },
    });

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [hasLocalChanges, setHasLocalChanges] = useState(false);

    // Hydrate form when profile data loads (SSR-friendly)
    useEffect(() => {
        if (profileData) {
            let contactData: Partial<ContactInfoFormData> = {};

            // Primary data source: SmartApplyApplicationState (draft data)
            if (
                profileData.smartApplyApplicationState?.__typename === 'SmartApplyApplicationState'
            ) {
                const smartApplyContactInfo = profileData.smartApplyApplicationState.contactInfo;
                if (smartApplyContactInfo) {
                    contactData = {
                        firstName: smartApplyContactInfo.firstName || '',
                        lastName: smartApplyContactInfo.lastName || '',
                        email: smartApplyContactInfo.email || '',
                        phone: smartApplyContactInfo.phoneNumber || '',
                        address: {
                            street: '', // Not available in GraphQL schema
                            city: smartApplyContactInfo.city || '',
                            state: smartApplyContactInfo.area || '', // 'area' maps to state
                            zipCode: '', // Not available in GraphQL schema
                            country: smartApplyContactInfo.country || 'US',
                        },
                    };
                }
            }

            // TODO: Add fallback data source: JobSeekerProfile (default user data)
            // Note: JobSeekerProfile is not currently included in the GraphQL query
            // This would require updating the GraphQL query to include jobSeekerProfile data

            // Update form data if we have any contact information
            if (
                Object.values(contactData).some((value) =>
                    typeof value === 'string'
                        ? value.trim()
                        : typeof value === 'object'
                          ? Object.values(value || {}).some((v) => v?.trim())
                          : false,
                )
            ) {
                setFormData((prevData) => ({
                    ...prevData,
                    ...contactData,
                    address: { ...prevData.address, ...contactData.address },
                }));
                setHasLocalChanges(false);
            }
        }
    }, [profileData]);

    // Notify parent when changes occur
    useEffect(() => {
        onChange(hasLocalChanges);
    }, [hasLocalChanges, onChange]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        }

        setValidationErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const updateField = (field: string, value: string): void => {
        if (field.includes('.')) {
            // Handle nested address fields
            const [parent, child] = field.split('.');
            if (parent === 'address' && child) {
                setFormData((prev) => ({
                    ...prev,
                    address: {
                        ...prev.address,
                        [child as keyof typeof prev.address]: value,
                    },
                }));
            }
        } else {
            setFormData((prev) => ({ ...prev, [field]: value }));
        }

        setHasLocalChanges(true);

        // Clear field-specific error
        if (validationErrors[field]) {
            const newErrors = { ...validationErrors };
            delete newErrors[field];
            setValidationErrors(newErrors);
        }
    };

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (!validateForm()) return;

        // Delegate to parent page for API call and navigation
        await onSubmit(formData);
        setHasLocalChanges(false);
    };

    // Loading state during profile fetch
    if (profileLoading) {
        return (
            <Box
                sx={{
                    backgroundColor: 'background.default',
                    padding: 6,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'border.subtle',
                    textAlign: 'center',
                }}
            >
                <Text sx={{ color: 'foreground.secondary', fontSize: 2 }}>
                    🔄 Loading your profile...
                </Text>
            </Box>
        );
    }

    // Error state if profile fetch failed
    if (profileError) {
        return (
            <Box
                sx={{
                    backgroundColor: 'background.default',
                    padding: 6,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'border.subtle',
                }}
            >
                <Text sx={{ color: 'feedback.negative', fontSize: 2, marginBlockEnd: 3 }}>
                    ❌ Error loading profile: {profileError.message}
                </Text>
                <Button variant="secondary" onClick={() => window.location.reload()}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                backgroundColor: 'background.default',
                padding: 6,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'border.subtle',
            }}
        >
            <Heading
                as="h2"
                level={2}
                sx={{
                    color: 'foreground.primary',
                    marginBlockEnd: 2,
                }}
            >
                Contact Information
            </Heading>
            <Text sx={{ color: 'foreground.secondary', fontSize: 2, marginBlockEnd: 6 }}>
                Please provide your contact information to continue with your application.
            </Text>

            {/* Page-level state indicators */}
            {hasUnsavedChanges && (
                <Box
                    sx={{
                        backgroundColor: 'warning.subtle',
                        padding: 3,
                        borderRadius: 1,
                        marginBlockEnd: 4,
                    }}
                >
                    <Text sx={{ color: 'warning.strong', fontWeight: 'bold' }}>
                        ⚠️ You have unsaved changes
                    </Text>
                </Box>
            )}

            {submitError && (
                <Box
                    sx={{
                        backgroundColor: 'feedback.negative',
                        padding: 3,
                        borderRadius: 1,
                        marginBlockEnd: 4,
                    }}
                >
                    <Text sx={{ color: 'white' }}>❌ Error: {submitError}</Text>
                </Box>
            )}

            <Box as="form" onSubmit={handleSubmit}>
                {/* Personal Information Section */}
                <Box sx={{ marginBlockEnd: 6 }}>
                    <Heading
                        as="h3"
                        level={4}
                        sx={{ marginBlockEnd: 4, color: 'foreground.primary' }}
                    >
                        Personal Information
                    </Heading>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: ['1fr', '1fr 1fr'],
                            gap: 4,
                        }}
                    >
                        <Box>
                            <Box
                                as="label"
                                sx={{ display: 'block', marginBlockEnd: 2, fontWeight: 'bold' }}
                            >
                                First Name *
                            </Box>
                            <Box
                                as="input"
                                type="text"
                                value={formData.firstName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    updateField('firstName', e.target.value)
                                }
                                placeholder="Enter your first name"
                                disabled={isSubmitting}
                                sx={{
                                    inlineSize: '100%',
                                    padding: 3,
                                    border: '1px solid',
                                    borderColor: validationErrors.firstName
                                        ? 'feedback.negative'
                                        : 'border.default',
                                    borderRadius: 1,
                                    fontSize: 2,
                                    opacity: isSubmitting ? 0.6 : 1,
                                    '&:focus': {
                                        outline: 'none',
                                        borderColor: 'primary.default',
                                    },
                                }}
                            />
                            {validationErrors.firstName && (
                                <Text
                                    sx={{
                                        color: 'feedback.negative',
                                        fontSize: 1,
                                        marginBlockStart: 1,
                                    }}
                                >
                                    {validationErrors.firstName}
                                </Text>
                            )}
                        </Box>
                        <Box>
                            <Box
                                as="label"
                                sx={{ display: 'block', marginBlockEnd: 2, fontWeight: 'bold' }}
                            >
                                Last Name *
                            </Box>
                            <Box
                                as="input"
                                type="text"
                                value={formData.lastName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    updateField('lastName', e.target.value)
                                }
                                placeholder="Enter your last name"
                                disabled={isSubmitting}
                                sx={{
                                    inlineSize: '100%',
                                    padding: 3,
                                    border: '1px solid',
                                    borderColor: validationErrors.lastName
                                        ? 'feedback.negative'
                                        : 'border.default',
                                    borderRadius: 1,
                                    fontSize: 2,
                                    opacity: isSubmitting ? 0.6 : 1,
                                    '&:focus': {
                                        outline: 'none',
                                        borderColor: 'primary.default',
                                    },
                                }}
                            />
                            {validationErrors.lastName && (
                                <Text
                                    sx={{
                                        color: 'feedback.negative',
                                        fontSize: 1,
                                        marginBlockStart: 1,
                                    }}
                                >
                                    {validationErrors.lastName}
                                </Text>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Contact Details Section */}
                <Box sx={{ marginBlockEnd: 6 }}>
                    <Heading
                        as="h3"
                        level={4}
                        sx={{ marginBlockEnd: 4, color: 'foreground.primary' }}
                    >
                        Contact Details
                    </Heading>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Box>
                            <Box
                                as="label"
                                sx={{ display: 'block', marginBlockEnd: 2, fontWeight: 'bold' }}
                            >
                                Email Address *
                            </Box>
                            <Box
                                as="input"
                                type="email"
                                value={formData.email}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    updateField('email', e.target.value)
                                }
                                placeholder="Enter your email address"
                                disabled={isSubmitting}
                                sx={{
                                    inlineSize: '100%',
                                    padding: 3,
                                    border: '1px solid',
                                    borderColor: validationErrors.email
                                        ? 'feedback.negative'
                                        : 'border.default',
                                    borderRadius: 1,
                                    fontSize: 2,
                                    opacity: isSubmitting ? 0.6 : 1,
                                    '&:focus': {
                                        outline: 'none',
                                        borderColor: 'primary.default',
                                    },
                                }}
                            />
                            {validationErrors.email && (
                                <Text
                                    sx={{
                                        color: 'feedback.negative',
                                        fontSize: 1,
                                        marginBlockStart: 1,
                                    }}
                                >
                                    {validationErrors.email}
                                </Text>
                            )}
                        </Box>
                        <Box>
                            <Box
                                as="label"
                                sx={{ display: 'block', marginBlockEnd: 2, fontWeight: 'bold' }}
                            >
                                Phone Number *
                            </Box>
                            <Box
                                as="input"
                                type="tel"
                                value={formData.phone}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    updateField('phone', e.target.value)
                                }
                                placeholder="Enter your phone number"
                                disabled={isSubmitting}
                                sx={{
                                    inlineSize: '100%',
                                    padding: 3,
                                    border: '1px solid',
                                    borderColor: validationErrors.phone
                                        ? 'feedback.negative'
                                        : 'border.default',
                                    borderRadius: 1,
                                    fontSize: 2,
                                    opacity: isSubmitting ? 0.6 : 1,
                                    '&:focus': {
                                        outline: 'none',
                                        borderColor: 'primary.default',
                                    },
                                }}
                            />
                            {validationErrors.phone && (
                                <Text
                                    sx={{
                                        color: 'feedback.negative',
                                        fontSize: 1,
                                        marginBlockStart: 1,
                                    }}
                                >
                                    {validationErrors.phone}
                                </Text>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Address Information Section */}
                <Box sx={{ marginBlockEnd: 6 }}>
                    <Heading
                        as="h3"
                        level={4}
                        sx={{ marginBlockEnd: 4, color: 'foreground.primary' }}
                    >
                        Address Information
                    </Heading>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {/* Street Address field removed - not available in OneGraph SmartApplyApplicationState schema */}
                        {/*
                        <Box>
                            <Box
                                as="label"
                                sx={{ display: 'block', marginBottom: 2, fontWeight: 'bold' }}
                            >
                                Street Address
                            </Box>
                            <Box
                                as="input"
                                type="text"
                                value={formData.address.street}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    updateField('address.street', e.target.value)
                                }
                                placeholder="Enter your street address"
                                disabled={isSubmitting}
                                sx={{
                                    width: '100%',
                                    padding: 3,
                                    border: '1px solid',
                                    borderColor: 'border.default',
                                    borderRadius: 1,
                                    fontSize: 2,
                                    opacity: isSubmitting ? 0.6 : 1,
                                    '&:focus': {
                                        outline: 'none',
                                        borderColor: 'primary.default',
                                    },
                                }}
                            />
                        </Box>
                        */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: ['1fr', '1fr 1fr 1fr'],
                                gap: 4,
                            }}
                        >
                            <Box>
                                <Box
                                    as="label"
                                    sx={{
                                        display: 'block',
                                        marginBlockEnd: 2,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    City
                                </Box>
                                <Box
                                    as="input"
                                    type="text"
                                    value={formData.address.city}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        updateField('address.city', e.target.value)
                                    }
                                    placeholder="Enter your city"
                                    disabled={isSubmitting}
                                    sx={{
                                        inlineSize: '100%',
                                        padding: 3,
                                        border: '1px solid',
                                        borderColor: 'border.default',
                                        borderRadius: 1,
                                        fontSize: 2,
                                        opacity: isSubmitting ? 0.6 : 1,
                                        '&:focus': {
                                            outline: 'none',
                                            borderColor: 'primary.default',
                                        },
                                    }}
                                />
                            </Box>
                            <Box>
                                <Box
                                    as="label"
                                    sx={{
                                        display: 'block',
                                        marginBlockEnd: 2,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    State
                                </Box>
                                <Box
                                    as="select"
                                    value={formData.address.state}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                        updateField('address.state', e.target.value)
                                    }
                                    disabled={isSubmitting}
                                    sx={{
                                        inlineSize: '100%',
                                        padding: 3,
                                        border: '1px solid',
                                        borderColor: 'border.default',
                                        borderRadius: 1,
                                        fontSize: 2,
                                        opacity: isSubmitting ? 0.6 : 1,
                                        '&:focus': {
                                            outline: 'none',
                                            borderColor: 'primary.default',
                                        },
                                    }}
                                >
                                    {US_STATES.map((state) => (
                                        <option key={state.value} value={state.value}>
                                            {state.label}
                                        </option>
                                    ))}
                                </Box>
                            </Box>
                            {/* ZIP Code field removed - not available in OneGraph SmartApplyApplicationState schema */}
                            {/*
                            <Box>
                                <Box
                                    as="label"
                                    sx={{
                                        display: 'block',
                                        marginBottom: 2,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    ZIP Code
                                </Box>
                                <Box
                                    as="input"
                                    type="text"
                                    value={formData.address.zipCode}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        updateField('address.zipCode', e.target.value)
                                    }
                                    placeholder="Enter ZIP code"
                                    disabled={isSubmitting}
                                    sx={{
                                        width: '100%',
                                        padding: 3,
                                        border: '1px solid',
                                        borderColor: 'border.default',
                                        borderRadius: 1,
                                        fontSize: 2,
                                        opacity: isSubmitting ? 0.6 : 1,
                                        '&:focus': {
                                            outline: 'none',
                                            borderColor: 'primary.default',
                                        },
                                    }}
                                />
                            </Box>
                            */}
                        </Box>
                    </Box>
                </Box>

                {/* Form Actions */}
                <Box sx={{ display: 'flex', gap: 3, justifyContent: 'flex-end' }}>
                    {canGoBack && (
                        <Button variant="secondary" onClick={onBack} disabled={isSubmitting}>
                            Back
                        </Button>
                    )}
                    <Button type="submit" variant="primary" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Continue'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default ContactInfoForm;
